//@ts-check
import React, { useContext } from 'react'
import {
  CAMPAIGN_BUILDER_CARD_OPTIONS,
  CAMPAIGN_CARD_ACTIONS,
  campaignCardOptions,
  EMAIL_TYPE,
} from '../options'
import { authorization } from '../../../../../../Module/Auth/Auth'
import { useDispatch, useSelector } from 'react-redux'
import { SnackBarContext } from '../../../../../../Components/SnackBar'
import { apiClient } from '../../../../../../Module/API/Client'
import { updateCampaignExtraData } from '../../../../../Campaigns/Page/Codes/gql/updateCampaignInternalStatus'
import {
  showReduxModal,
  hideReduxModal,
  resetReduxModal,
} from '../../../../../../Reducers/UI/actions'
import {
  openLinkInNewTab,
  getCampaignBuilderPageUrl,
  getDashboardPageUrl,
  getClientDashboardPageUrl,
  getDigitalProofPageUrl,
  getCodesPageUrl,
  addAuthParamsToCampaignBuilderUrl,
  CampaignContext,
  useCancelPayment,
  CAMPAIGNS_WITH_NO_PAYMENT,
  handleUpdateExtraData,
  getEditPageUrl,
} from '../utils'
import { useStyles } from '../style'
import { getCampaignFlyerInfo, updateSelfServeCampaignData } from '../graphQL'
import { updateCampaign } from '../../../../../../Module/graphQL'
import {
  CAMPAIGN_INTERNAL_STATUSES,
  CAMPAIGN_LOG_ENUM_TYPES,
  STATUS_CAMPAIGN_ENUM_ACTIVE,
  STATUS_CAMPAIGN_ENUM_ARCHIVE,
} from '../../../../../../shared/constants'
import { insertCampaignLogs } from '../../../../../../Logs/campaign/gql'
import { queryClient } from '../../../../../../Module/Query'
/** @import { Campaign } from '../utils' */
/** @import { Option } from '../../../../../../Components/ActionMenu' */

/**
 * @param {Campaign} campaign 
 * @returns {Option[]}
 */
export function useGetActionsCard(campaign) {
  const user = authorization.getUser()
  const userId = user.id
  const { country, setCampaignEmailData } = useContext(CampaignContext)
  const countryCode = country.code
  const dispatch = useDispatch()
  const runSnackBar = useContext(SnackBarContext)
  const currentClient = useSelector(state => state.clients.clientView)
  const classes = useStyles()
  const { refundPayment } = useRefundPayment()
  const { cancelPayment } = useCancelPayment()
  const origin = window.location.origin
  const key = campaign.internalStatus
  const {
    campaignId,
    campaignName,
    isPaymentSkipped,
    paymentIntent,
    internalStatus,
    clientId,
    type,
    subtype,
  } = campaign

  const canNavigateCampaignBuilder =
    !authorization.isClient() ||
    (currentClient && currentClient.isSelfServeEnabled)

  const OPTIONS = {
    [CAMPAIGN_CARD_ACTIONS.CAMPAIGN_BUILDER]: {
      name: 'Campaign Builder',
      url: () => {
        const campaignBuilderPageUrl = getCampaignBuilderPageUrl({
          campaignId,
          clientId,
          lastActiveStep: 'audience',
          channel: type,
          subtype: subtype,
        })
        const url = addAuthParamsToCampaignBuilderUrl(
          campaignBuilderPageUrl,
          clientId,
        )
        return url.toString()
      },
    },
    [CAMPAIGN_CARD_ACTIONS.CAMPAIGN_DASHBOARD]: {
      name: 'Campaign Dashboard',
      url: () => {
        const url = getDashboardPageUrl(campaignId, origin, type, subtype)
        return url
      },
    },
    [CAMPAIGN_CARD_ACTIONS.CLIENT_EMAIL]: {
      name: 'Email Client',
      onClick: () => {
        setCampaignEmailData({ ...campaign, emailType: EMAIL_TYPE.DEFAULT })
      },
    },
    [CAMPAIGN_CARD_ACTIONS.ARCHIVE_CAMPAIGN]: {
      name: 'Archive Campaign',
      onClick: () => {
        dispatch(
          showReduxModal({
            title: `Archive the campaign?`,
            children: (
              <div>
                <div
                  className={classes.modalText}
                >{`Campaign ${campaignName} will be archived.`}</div>
                <div className={classes.modalText}>
                  Client will not be able to resume editing this campaign. The
                  client will have to create another campaign if they wish to
                  restart the process
                </div>
              </div>
            ),
            onConfirm: async () => {
              try {
                if (
                  !isPaymentSkipped &&
                  !CAMPAIGNS_WITH_NO_PAYMENT.includes(campaignId)
                ) {
                  if (
                    ![
                      CAMPAIGN_INTERNAL_STATUSES.DRAFT,
                      CAMPAIGN_INTERNAL_STATUSES.IN_REVIEW,
                      CAMPAIGN_INTERNAL_STATUSES.REJECTED,
                    ].includes(internalStatus)
                  ) {
                    await refundPayment({
                      campaignId,
                      paymentIntent,
                      countryCode,
                      userId,
                    })
                  }
                  if (
                    internalStatus === CAMPAIGN_INTERNAL_STATUSES.IN_REVIEW
                  ) {
                    await cancelPayment({
                      campaignId,
                      paymentIntent,
                      countryCode,
                      userId,
                    })
                  }
                }
                await updateSelfServeCampaignData({
                  campaignId,
                  campaignDetailedCosts: null,
                  internalStatus: CAMPAIGN_INTERNAL_STATUSES.ARCHIVED,
                })
                await handleSendToArchive(campaignId, internalStatus)
                runSnackBar({
                  type: 'success',
                  msg: `Campaign successfully archived`,
                })
                onCancel()
              } catch (error) {
                console.error(error)
              }
            },
            onCancel,
          }),
        )
      },
      style: {
        color: '#EE4343',
      },
    },
    [CAMPAIGN_CARD_ACTIONS.FLYERS_DESIGN]: {
      name: 'Flyer Design',
      onClick: async () => {
        const request = await getCampaignFlyerInfo(campaignId)
        const {
          data: {
            campaignFlyerInfo: { s3Link },
          },
        } = await request.json()
        if (s3Link) {
          openLinkInNewTab(s3Link)
        } else {
          console.error('s3Link is missing')
        }
      },
    },
    [CAMPAIGN_CARD_ACTIONS.SEND_TO_DRAFT]: {
      name: 'Send to Draft',
      onClick: () => {
        dispatch(
          showReduxModal({
            title: `Move campaign back to Draft?`,
            children: (
              <div>
                <div
                  className={classes.modalText}
                >{`Campaign: ${campaignName}`}</div>
                <div className={classes.modalText}>
                  This campaign will be moved back to Draft status. Any
                  progress made through the campaign pipeline will be lost
                  (data such as codes will remain). The client will have to
                  re-submit the campaign if they wish to restart the approval
                  process.
                </div>
              </div>
            ),
            onConfirm: async () => {
              try {
                await refundPayment({
                  campaignId,
                  paymentIntent,
                  countryCode,
                  userId,
                })
                await updateSelfServeCampaignData({
                  campaignId,
                  campaignDetailedCosts: null,
                })
                await handleUpdateExtraData({
                  campaignId,
                  internalStatus: CAMPAIGN_INTERNAL_STATUSES.DRAFT,
                })
                runSnackBar({
                  type: 'success',
                  msg: `Campaign successfully moved to Draft`,
                })
                onCancel()
              } catch (error) {
                console.error(error)
              }
            },
            onCancel,
          }),
        )
      },
    },
    [CAMPAIGN_CARD_ACTIONS.APPROVE_CAMPAIGN]: {
      name: 'Approve campaign',
      onClick: async () => {
        const url = getCodesPageUrl(campaignId, origin)
        setCampaignEmailData({
          ...campaign,
          emailType: EMAIL_TYPE.APPROVE_CAMPAIGN,
          codesPageUrl: url,
          paymentIntent,
        })
      },
      style: {
        color: '#31955F',
      },
    },
    [CAMPAIGN_CARD_ACTIONS.DECLINE_CAMPAIGN]: {
      name: 'Decline campaign',
      onClick: () => {
        const url = getClientDashboardPageUrl(campaignId, origin)
        setCampaignEmailData({
          ...campaign,
          emailType: EMAIL_TYPE.DECLINE_CAMPAIGN,
          clientDashboardUrl: url,
          paymentIntent,
        })
      },
      style: {
        color: '#EE4343',
      },
    },
    [CAMPAIGN_CARD_ACTIONS.CAMPAIGN_BUILDER_EDIT]: {
      name: 'Edit campaign',
      url: () => {
        const campaignBuilderPageUrl = getCampaignBuilderPageUrl({
          campaignId,
          clientId,
          lastActiveStep: 'audience',
          channel: type,
          subtype: subtype,
        })

        const url = addAuthParamsToCampaignBuilderUrl(
          campaignBuilderPageUrl,
          clientId,
        )

        return url.toString()
      },
    },
    [CAMPAIGN_CARD_ACTIONS.CODES_PAGE]: {
      name: 'Codes Page',
      url: () => {
        const url = getCodesPageUrl(campaignId, origin)
        return url
      },
    },
    [CAMPAIGN_CARD_ACTIONS.DIGITAL_PROOF_PAGE]: {
      name: 'Internal Files Page',
      url: () => {
        const url = getDigitalProofPageUrl(campaignId, origin)
        return url
      },
    },
    [CAMPAIGN_CARD_ACTIONS.ACTIVATE_CAMPAIGN]: {
      name: 'Activate campaign',
      onClick: async () => {
        await handleUpdateExtraData({
          campaignId,
          internalStatus: CAMPAIGN_INTERNAL_STATUSES.ACTIVE,
        })
        await updateCampaign({
          campaignId,
          status: STATUS_CAMPAIGN_ENUM_ACTIVE,
        })
        await insertCampaignLogs([
          {
            campaignId,
            type: CAMPAIGN_LOG_ENUM_TYPES.ACTIVATE_CAMPAIGN,
          },
        ])
      },
    },
    [CAMPAIGN_CARD_ACTIONS.CAMPAIGN_SETTINGS]: {
      name: 'Campaign Settings',
      url: () => getEditPageUrl(campaignId, origin),
    },
  }

  const campaignCardOption = campaignCardOptions[key]
  if (campaignCardOption) {
    const options = campaignCardOptions[key]
      .filter(currentOption => {
        if (CAMPAIGN_BUILDER_CARD_OPTIONS.has(currentOption)) {
          return canNavigateCampaignBuilder
        }
        return true
      })
      .map(currentOption => {
        if (OPTIONS[currentOption]) return OPTIONS[currentOption]
        if (currentOption === "DIVIDER") return "DIVIDER"
        return null
      })
      .filter(x => x !== null)
    return options
  }

  console.warn(`no campaignCardOption for ${key}`)
  return []
}

function useRefundPayment() {
  const runSnackBar = useContext(SnackBarContext)
  async function refundPayment({
    campaignId,
    paymentIntent,
    countryCode,
    userId,
  }) {
    const res = await apiClient.refundPayment({
      paymentIntent,
      countryCode,
      campaignId,
      userId,
    })
    if (res && res.status === 200) {
      const { paymentStatus } = await res.json()
      runSnackBar({
        type: 'success',
        msg: `Payment refunded`,
      })
      return await updateCampaignExtraData({
        campaignId,
        paymentStatus,
      })
    } else {
      runSnackBar({
        type: 'error',
        msg: `Payment error`,
      })
      throw Error('Payment error')
    }
  }
  return { refundPayment }
}

function onCancel() {
  hideReduxModal()
  resetReduxModal()
}

/**
  * @param {string} campaignId 
  * @param {string} stage 
  */
async function handleSendToArchive(campaignId, stage) {
  await updateCampaign({
    campaignId,
    status: STATUS_CAMPAIGN_ENUM_ARCHIVE,
  })
  await insertCampaignLogs([
    {
      campaignId,
      type: CAMPAIGN_LOG_ENUM_TYPES.ARCHIVE_CAMPAIGN,
      additionalInfo: `internal status ${stage}`,
    },
  ])
  queryClient.invalidateQueries('CampaignPipelineData')
}
